import React, { useState } from "react";
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TablePagination,
    Paper,
    Box,
    Typography,
    Button,
    Stack,
    Select,
    MenuItem,
    Chip,
    Menu,
    Collapse,
} from "@mui/material";
import theme from "../utils/Theme";
import { useTranslation } from "react-i18next";

const GenericTable = ({
    title,
    columns,
    data,
    actions = [],
    rowsPerPageOptions = [5, 10, 25],
    onUpdate,
    selectedElement,
    onSelect,
    renderExpandedRow,
}) => {
    const { t } = useTranslation();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
    const [statusAnchorEl, setStatusAnchorEl] = useState(null);
    const [rowIdStatusToChange, setRowStatusToChange] = useState(null);

    const [expandedRowId, setExpandedRowId] = useState([]);


    const handleChangePage = (_, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleStatusChange = (row, field, newValue, event) => {
        event.stopPropagation();
        const oldValue = row[field];
        if (onUpdate) {
            onUpdate(row, field, oldValue, newValue);
        }
        setStatusAnchorEl(null);
    };

    const isSelected = (row) => {
        return selectedElement && row.id === selectedElement.id;
    };



    const handleStatusMenuOpen = (event, row) => {
        event.stopPropagation();
        setStatusAnchorEl(event.currentTarget);
        setRowStatusToChange(row);
    };

    const handleMenuClose = () => {
        setStatusAnchorEl(null);
        setRowStatusToChange(null);
    };

    return (
        <Box>
            {title && (
                <Typography variant="h5" fontWeight="bold" mb={2} sx={{ color: "black" }}>
                    {title}
                </Typography>
            )}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead sx={{ backgroundColor: theme.palette.table.headerBackground }}>
                        <TableRow>
                            {columns.map((col) => (
                                <TableCell key={col.field} align={col.align || "left"}>
                                    <strong style={{ color: theme.palette.sidebar.background }}>{col.label}</strong>
                                </TableCell>
                            ))}
                            {actions.length > 0 && (
                                <TableCell align="center">
                                    <strong style={{ color: theme.palette.sidebar.background }}>Actions</strong>
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, rowIndex) => {
                            return <React.Fragment key={row.id || rowIndex}>
                                <TableRow
                                    onClick={() => {
                                        if (renderExpandedRow) {
                                            setExpandedRowId(expandedRowId === row.id ? null : row.id);
                                        } else if (onSelect) {
                                            onSelect(row);
                                        }
                                    }}
                                    sx={{
                                        backgroundColor: isSelected(row) ? "#d6e4ff" : "white",
                                        cursor: "pointer",
                                    }}
                                >
                                    {columns.map((col) => (
                                        <TableCell key={col.field} align={col.align || "left"} sx={{ color: "black" }}>
                                            {col.editable ? (
                                                col.type === "select" ? (
                                                    <Select
                                                        value={row[col.field] || ""}
                                                        onChange={(event) => handleStatusChange(row, col.field, event.target.value)}
                                                        sx={{
                                                            minWidth: 120,
                                                            color: "black",
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: "black",
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: "black",
                                                            },
                                                            '& .MuiSelect-select': {
                                                                color: 'black',
                                                            },
                                                            '& .MuiSelect-icon': {
                                                                color: 'black',
                                                            }
                                                        }}
                                                        MenuProps={{
                                                            PaperProps: {
                                                                sx: {
                                                                    bgcolor: "white",
                                                                    color: "black",
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        {col.options.map((option) => (
                                                            <MenuItem
                                                                key={option.value}
                                                                value={option.value}
                                                                sx={{
                                                                    color: "black",
                                                                    backgroundColor: "white",
                                                                    '&:hover': {
                                                                        backgroundColor: "#f0f0f0",
                                                                    }
                                                                }}
                                                            >
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                ) : col.type === 'status' ? (
                                                    <>
                                                        <Chip
                                                            label={t(`statuses.${row[col.field]}`)}
                                                            size="small"
                                                            onClick={(event) => handleStatusMenuOpen(event, row)}
                                                            sx={{
                                                                backgroundColor: 'lightgray',
                                                                color: 'black',
                                                                borderRadius: '16px',
                                                                '&:hover': {
                                                                    backgroundColor: 'darkgray',
                                                                },
                                                            }}
                                                        />

                                                        <Menu
                                                            anchorEl={statusAnchorEl}
                                                            open={Boolean(statusAnchorEl)}
                                                            onClose={handleMenuClose}
                                                        >
                                                            {col.options.map((status) => (
                                                                <MenuItem
                                                                    key={status.value}
                                                                    value={status.value} sx={{
                                                                        color: 'black',
                                                                        '&:hover': {
                                                                            backgroundColor: 'darkgray',
                                                                        },
                                                                    }} onClick={(event) => handleStatusChange(rowIdStatusToChange, 'status', status.value, event)}>
                                                                    {status.label}
                                                                </MenuItem>
                                                            ))}
                                                        </Menu>
                                                    </>
                                                ) : (
                                                    <input
                                                        key={`${col.id}-${rowIndex}-${col.field}`}
                                                        type="text"
                                                        value={row[col.field] || ""}
                                                        onChange={(e) => handleStatusChange(row, col.field, e.target.value)}
                                                        style={{ backgroundColor: 'transparent', border: "1px solid #ccc", padding: "5px" }}
                                                    />
                                                )
                                            ) : (
                                                col.type === 'hours' ? (
                                                    `${Math.floor(getNestedValue(row, col.field) / 60)}h ${getNestedValue(row, col.field) % 60}m (${getNestedValue(row, col.field)} min)`
                                                ) : col.type === 'select' ? (
                                                    t(`statuses.${getNestedValue(row, col.field)}`)
                                                ) : col.type === 'title' ? (
                                                    <Typography fontWeight={"bold"}>
                                                        {getNestedValue(row, col.field)}
                                                    </Typography>
                                                ) : col.type === 'status' ? (
                                                    <Chip
                                                        label={t(`statuses.${getNestedValue(row, col.field)}`)}
                                                        sx={{
                                                            backgroundColor: 'lightgray',
                                                            color: 'white',
                                                        }}
                                                        size="small"
                                                    />
                                                ) : col.type === 'date' ? (
                                                    getNestedValue(row, col.field)
                                                        ? new Date(getNestedValue(row, col.field)).toLocaleDateString()
                                                        : 'N/A'
                                                ) : (
                                                    <Typography
                                                        sx={{
                                                            maxWidth: 200, // or whatever width you need
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                        {getNestedValue(row, col.field)}
                                                    </Typography>
                                                )
                                            )}
                                        </TableCell>
                                    ))}
                                    {actions.length > 0 && (
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                {actions.map((action, i) => (
                                                    <Button
                                                        key={i}
                                                        onClick={() => action.onClick(row)}
                                                        variant="text"
                                                        sx={{ minWidth: 0, padding: 1, color: "black" }}
                                                    >
                                                        {action.icon}
                                                    </Button>
                                                ))}
                                            </Stack>
                                        </TableCell>
                                    )}
                                </TableRow>
                                {renderExpandedRow && (
                                    <TableRow>
                                        <TableCell
                                            colSpan={columns.length + (actions.length > 0 ? 1 : 0)}
                                            sx={{ padding: 0, margin: 0, borderBottom: "1px solid rgba(0, 0, 0, 1)" }}
                                        >
                                            <Collapse in={expandedRowId === row.id} timeout="auto" unmountOnExit>
                                                <Box sx={{ padding: 0, margin: 0 }}>
                                                    {renderExpandedRow(row)}
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </React.Fragment>
                        })}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    sx={{
                        backgroundColor: theme.palette.table.footerBackground,
                        color: "black",
                        "& .MuiTablePagination-selectLabel, \
                           & .MuiTablePagination-displayedRows, \
                           & .MuiTablePagination-actions, \
                           & .MuiSelect-select, \
                           & .MuiSelect-icon, \
                           & .MuiInputBase-root, \
                           & .MuiOutlinedInput-notchedOutline, \
                           & .MuiSvgIcon-root": {
                            color: "black",
                            borderColor: "black",
                        },
                        "& .MuiSelect-select": {
                            paddingRight: "32px", // to not cut off icon
                        },
                        "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                                borderColor: "black",
                            },
                            "&:hover fieldset": {
                                borderColor: "black",
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: "black",
                            },
                        },
                        "& .MuiMenu-paper": {
                            backgroundColor: "white",
                            color: "black",
                        },
                        "& .MuiMenuItem-root": {
                            color: "black",
                            "&:hover": {
                                backgroundColor: "#f0f0f0",
                            },
                        },
                    }}
                    SelectProps={{
                        MenuProps: {
                            PaperProps: {
                                sx: {
                                    backgroundColor: "white",
                                    color: "black",
                                    "& .MuiMenuItem-root": {
                                        color: "black",
                                        "&:hover": {
                                            backgroundColor: "#f0f0f0",
                                        },
                                    },
                                },
                            },
                        },
                    }}
                    count={data.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={rowsPerPageOptions}
                />
            </TableContainer>
        </Box>
    );
};

const getNestedValue = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

export default GenericTable;
